import { IKuikaDataTypeField } from "@kuika/kuika-cl-model";
import { FC } from "react";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, {
  KuikaSelectboxOption,
  KuikaSelectboxOptionGroup
} from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../ui-kit/kuika-switch/kuika-switch";
import "./object-type-builder.scss";

/**
 * Scalar field types offered by the builder. Values are the backend `TypeName` strings the code
 * generator maps to C# (see DomainTypeEmitHelper.MapTsToCsPropertyType) — keep them in sync.
 */
export const SCALAR_TYPE_OPTIONS: KuikaSelectboxOption[] = [
  { value: "String", label: "String" },
  { value: "Boolean", label: "Boolean" },
  { value: "Int32", label: "Integer" },
  { value: "Int64", label: "Long" },
  { value: "Double", label: "Double" },
  { value: "Decimal", label: "Decimal" },
  { value: "DateTime", label: "DateTime" },
  { value: "Guid", label: "Guid" }
];

/**
 * The one identifier rule for catalog TYPE and FIELD names — both are emitted verbatim into
 * generated C#, so only ASCII survives. Deliberately shared (KOA-18293 B4): the two surfaces
 * used to carry the same regex twice with drifting error copy.
 */
export const EMITTED_NAME_REGEX: RegExp = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * "letters" alone misled Turkish users — İ, ç, ş ARE letters, just not emittable ones
 * (KOA-18293 B4). Say exactly which letters qualify.
 */
export const emittedNameRuleMessage = (kind: "type" | "field"): string =>
  `Enter a valid ${kind} name (English letters A–Z, digits, underscore).`;

/**
 * The C# property name a field is emitted with: the field name with an upper-cased first character.
 * Mirrors `KuikaDataTypeCatalog.EmittedPropertyName` on the backend, which is what makes two fields
 * differing only in that character collide on one generated property. Uses an explicit `en-US` locale
 * so a Turkish-locale designer does not turn "id" into "İd" and disagree with the generator.
 */
export const emittedPropertyName = (fieldName: string): string =>
  fieldName ? fieldName.charAt(0).toLocaleUpperCase("en-US") + fieldName.slice(1) : fieldName;

/**
 * Why a field cannot be generated, or null when it is fine. Mirrors the backend catalog validations
 * (KV1252 / KV1253 / KV1254) so the problem is caught while authoring instead of when code generation
 * is refused: names are emitted verbatim as C# properties, two fields must not collapse onto one
 * property, and a property cannot be named after its own class.
 */
export const getFieldNameError = (
  fields: IKuikaDataTypeField[],
  index: number,
  ownerTypeName?: string
): string | null => {
  const name: string = fields[index]?.name ?? "";
  if (!name) return "Field name is required.";
  if (!EMITTED_NAME_REGEX.test(name)) return emittedNameRuleMessage("field");

  const emitted: string = emittedPropertyName(name);
  const collides: boolean = fields.some(
    (f: IKuikaDataTypeField, i: number): boolean => i !== index && !!f.name && emittedPropertyName(f.name) === emitted
  );
  if (collides) return `Another field already generates '${emitted}'.`;
  if (ownerTypeName && emitted === ownerTypeName) return "A field cannot have the same name as its data type.";
  return null;
};

/**
 * Why a catalog TYPE cannot be created under this name, or null when it is fine. Uniqueness is
 * CASE-INSENSITIVE (KOA-18293 B6): the emitted C# / SQL surfaces treat `izin` and `IZIN` as a
 * collision, so letting both live side by side in the catalog only defers the failure to codegen.
 * `otherTypeNames` is every catalog name EXCEPT the type being edited — the caller excludes it so
 * a case-only rename of a type to itself stays legal.
 */
export const getTypeNameError = (name: string, otherTypeNames: readonly string[]): string | null => {
  if (!name) return "Type name is required.";
  if (!EMITTED_NAME_REGEX.test(name)) return emittedNameRuleMessage("type");
  const lowered: string = name.toLowerCase();
  const duplicate: boolean = otherTypeNames.some((other: string): boolean => (other || "").toLowerCase() === lowered);
  if (duplicate) return "This type name is already used.";
  return null;
};

export interface ObjectTypeBuilderProps {
  /** Controlled list of fields for the type being edited. */
  fields: IKuikaDataTypeField[];
  onChange: (fields: IKuikaDataTypeField[]) => void;
  /**
   * Names of other catalog types that a field may reference (compose-by-reference). Excludes the
   * type currently being edited to avoid direct self-reference.
   */
  availableTypeNames?: string[];
  /** Name of the type being edited — a field may not generate a property with that same name. */
  ownerTypeName?: string;
  disabled?: boolean;
}

/**
 * Recursive-by-reference editor for the fields of a Kuika Data Type. Each field has a name, a type
 * (a scalar OR another catalog type's name) and an "is list" flag. Objects are composed by
 * referencing other catalog types rather than inline nesting — this keeps the editor flat and
 * pushes reuse into the catalog. Produces the canonical `IKuikaDataTypeField` shape.
 */
export const ObjectTypeBuilder: FC<ObjectTypeBuilderProps> = ({
  fields,
  onChange,
  availableTypeNames,
  ownerTypeName,
  disabled
}) => {
  const typeGroups: KuikaSelectboxOptionGroup[] = [{ label: "Primitive", options: SCALAR_TYPE_OPTIONS }];
  const referenceOptions: KuikaSelectboxOption[] = (availableTypeNames ?? []).map(
    (name: string): KuikaSelectboxOption => ({ value: name, label: name })
  );
  if (referenceOptions.length > 0) {
    typeGroups.push({ label: "Data types", options: referenceOptions });
  }

  const updateField = (index: number, patch: Partial<IKuikaDataTypeField>): void => {
    const next: IKuikaDataTypeField[] = fields.map((f: IKuikaDataTypeField, i: number): IKuikaDataTypeField =>
      i === index ? { ...f, ...patch } : f
    );
    onChange(next);
  };

  const removeField = (index: number): void => {
    onChange(fields.filter((_f: IKuikaDataTypeField, i: number): boolean => i !== index));
  };

  const addField = (): void => {
    onChange([...fields, { name: "", type: "String", isList: false }]);
  };

  return (
    <div className="kuika-object-type-builder">
      <div className="kuika-object-type-builder__header">
        <span className="kuika-object-type-builder__col-name">Name</span>
        <span className="kuika-object-type-builder__col-type">Type</span>
        <span className="kuika-object-type-builder__col-list">List</span>
        <span className="kuika-object-type-builder__col-actions" />
      </div>

      {fields.map((field: IKuikaDataTypeField, index: number) => {
        // An empty name is only surfaced once the user has started filling the row in, so a freshly
        // added field does not read as an error before it has been touched.
        const nameError: string | null = getFieldNameError(fields, index, ownerTypeName);
        const shownError: string | null = field.name ? nameError : null;
        return (
          <div key={index} className="kuika-object-type-builder__row">
            <div
              className={
                shownError
                  ? "kuika-object-type-builder__col-name kuika-object-type-builder__col-name--invalid"
                  : "kuika-object-type-builder__col-name"
              }
            >
              <KuikaInput
                value={field.name}
                placeholder="Field name"
                disabled={disabled}
                onChange={(_e, value: string | number): void => updateField(index, { name: String(value) })}
              />
              {shownError && <div className="kuika-object-type-builder__error">{shownError}</div>}
            </div>
            <div className="kuika-object-type-builder__col-type">
              <KuikaSelectbox
                groups={typeGroups}
                value={field.type}
                disabled={disabled}
                style={{ width: "100%" }}
                onChange={(value): void => updateField(index, { type: String(value) })}
              />
            </div>
            <div className="kuika-object-type-builder__col-list">
              <KuikaSwitch
                checked={!!field.isList}
                disabled={disabled}
                onChange={(checked: boolean): void => updateField(index, { isList: checked })}
              />
            </div>
            <div className="kuika-object-type-builder__col-actions">
              <KuikaButton
                type="tertiary"
                color="neutral"
                isIcon
                disabled={disabled}
                onClick={(): void => removeField(index)}
              >
                <KuikaSvg type="common" name="trash" color="var(--red-5)" />
              </KuikaButton>
            </div>
          </div>
        );
      })}

      {fields.length === 0 && (
        <div className="kuika-object-type-builder__empty">No fields yet. Add a field to describe this type.</div>
      )}

      <div className="kuika-object-type-builder__add">
        <KuikaButton htmlType="button" size="sm" type="tertiary" disabled={disabled} onClick={addField}>
          Add field
        </KuikaButton>
      </div>
    </div>
  );
};

export default ObjectTypeBuilder;
